/**
 * Timezone Override Module
 *
 * This module provides a solution to override the default JavaScript Date constructor
 * to ensure the browser shows the same clock time as provided by the server.
 *
 * The primary goal is to prevent the browser from automatically converting dates
 * to the local timezone when parsing ISO 8601 date strings (with T separator), which can cause
 * unexpected time shifts in applications that need to preserve the original time
 * while operating in the local timezone.
 *
 * @example
 * // Before override (with GMT+3 timezone):
 * // new Date('2025-08-12T15:00:00+02:00') → 2025-08-12T16:00:00+03:00 (time shifted)
 *
 * // After override (with GMT+3 timezone):
 * // new Date('2025-08-12T15:00:00+02:00') → 2025-08-12T15:00:00+03:00 (time preserved)
 */
export const overrideBrowserTimezone = () => {
  // Store original Date constructor
  const OriginalDate = Date;

  /**
   * ISO 8601 time regex pattern for validating date strings
   * Source: https://github.com/validatorjs/validator.js/blob/master/src/lib/isISO8601.js
   *
   * This regex validates the complete ISO 8601 format including:
   * - Date: YYYY-MM-DD
   * - Time: HH:MM:SS
   * - Timezone: Z or ±HH:MM
   * - Optional milliseconds and week-based dates
   */
  const iso8601StrictSeparator =
    /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-3])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T]((([01]\d|2[0-3])((:?)[0-5]\d)?|24:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;

  /**
   * Validates if a string conforms to ISO 8601 date format
   *
   * @param time - The string to validate
   * @returns True if the string matches ISO 8601 format, false otherwise
   */
  const isIsoTime = (time: string) => {
    return iso8601StrictSeparator.test(time);
  };

  /**
   * Overridden Date constructor that ignores timezone from the date string
   *
   * This constructor handles three main scenarios:
   * 1. No arguments: Returns current time (unchanged behavior)
   * 2. ISO 8601 string: Parses and preserves time in local timezone
   * 3. Other arguments: Falls back to original Date constructor behavior
   *
   * @param args - Arguments passed to the Date constructor
   * @returns A new Date object with the overridden timezone behavior
   */
  const DateOverride = function (this: any, ...args: any[]) {
    const calledWithNew = new.target !== undefined;

    if (!calledWithNew) {
      return OriginalDate();
    }

    if (args.length === 0) {
      // new Date() - current time
      return new OriginalDate();
    }

    if (
      args.length === 1 &&
      typeof args[0] === "string" &&
      isIsoTime(args[0])
    ) {
      // new Date(dateString) - parse string and convert to current timezone
      const noTz = args[0].replace(/([+-]\d{2}:\d{2}|Z)$/, "");
      // Split into parts
      const [datePart, timePart] = noTz.split("T");

      // FULL ISO 8601 DATE AND TIME (EXAMPLE: 2025-08-12T15:00:00+02:00)
      if (datePart && timePart) {
        const [year, month, day] = datePart.split("-").map(Number);
        const [hour, minute, second] = timePart.split(":").map(Number);

        // Month is 0-based in JS Date
        return new OriginalDate(
          year,
          month - 1,
          day,
          hour,
          minute,
          second || 0,
        );
      }

      // ONLY DATE (EXAMPLE: 2025-08-12, 2025-08, 2025)
      if (datePart && !timePart) {
        const [year, month, day] = datePart.split("-").map(Number);

        if (year && month && day) {
          return new OriginalDate(year, month - 1, day);
        }

        if (year && month) {
          return new OriginalDate(year, month - 1);
        }

        if (year) {
          return new OriginalDate(year, 0, 1);
        }
      }

      return new OriginalDate(args[0]); // Fallback to original parsing
    }

    return new OriginalDate(...(args as ConstructorParameters<typeof Date>));
  };

  // Copy static methods and properties from original Date constructor
  DateOverride.now = OriginalDate.now;
  DateOverride.parse = OriginalDate.parse;
  DateOverride.UTC = OriginalDate.UTC;
  DateOverride.prototype = OriginalDate.prototype;

  // Override global Date constructor
  (globalThis as any).Date = DateOverride;

  // Also override window.Date for browser environments
  if (typeof window !== "undefined") {
    (window as any).Date = DateOverride;
  }
};
